import {
	CalendarOutlined,
	CheckOutlined,
	LinkOutlined,
	SaveOutlined,
	UserOutlined,
} from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Button } from "components/Button/Button"
import { Disclosure } from "components/Disclosure/Disclosure"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { ShortcutLabel } from "components/ShortcutLabel/ShortcutLabel"
import { TagSelect } from "components/TagSelect/TagSelect"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { useDisclosure } from "hooks/useDisclosure"
import { WeightIcon } from "icons/WeightIcon"
import { ScreeningNote } from "interface/device"
import { Patient, PatientDetailItem } from "interface/patientType"
import {
	TemplatedFormParams,
	TemplatedFormParamsAnswer,
} from "interface/templatedForm"
import { groupBy } from "lodash-es"
import { Chat } from "modules/communications/Chat"
import { Voice } from "modules/communications/Voice"
import { TemplatedForm } from "modules/dashboard/TemplatedForm"
import { WeightLabel } from "modules/dashboard/WeightLabel"
import { CreateEscalation } from "modules/escalations/CreateEscalation"
import { useFlag } from "modules/flags/flags"
import {
	ComponentProps,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useParams } from "react-router"
import { useUnreadMessagesCount } from "services/communications"
import { useScreeningNote, useScreeningNoteUpsert } from "services/devices"
import { useConfigClinicalNoteLabels } from "services/globalConfig"
import {
	usePatientDetails,
	useProfileNote,
	useReviewDataPoint,
	useStudyTemplate,
} from "services/patients"
import { useOrganization, useUserRole } from "services/users"
import { cn } from "styles/cn"
import { copyToClipboard, hasSelected, humanReadable } from "utils"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { dateFormatter, today } from "utils/date"
import { getOS } from "utils/os"
import { EditProfileNote } from "./EditProfileNote"

export const ReviewForm = ({
	className,
	dataPoint,
	previusDataPoint,
	previousDataPoint = previusDataPoint,
	disabled,
	...props
}: ComponentProps<"div"> & {
	dataPoint: PatientDetailItem
	previusDataPoint?: PatientDetailItem
	previousDataPoint?: PatientDetailItem
	disabled?: boolean
}) => {
	const { id, capturedAt, isReviewed, reviewedAt } = dataPoint
	const discloseStudyForm = useDisclosure()
	const [copySuccess, setCopySuccess] = useState(false)
	const [form] = Form.useForm()
	const {
		data: { isProvider, isProviderAnon, studyTemplate: studyTemplateId } = {},
	} = useUserRole()
	const { userId: patientId } = useParams()
	const { notify } = useNotification()
	const { data: patient } = usePatientDetails(patientId)
	const reviewDataPoint = useReviewDataPoint(patientId!, id)
	const { data: studyQuestions = [] } = useStudyTemplate(studyTemplateId)
	const { clinicalNotes } = useFlag("clinicalNotes")
	const { isMac, isIOS } = getOS()
	const reviewButtonLabel = isReviewed
		? `Reviewed at ${dateFormatter(reviewedAt!, { hasYear: false })}`
		: disabled
			? "Waiting for image to load..."
			: "Mark as reviewed"
	const reviewDisabled = disabled || isReviewed || !isProvider
	const reviewDataPointHandler = useCallback(
		async (variables: TemplatedFormParams | null, onSuccess?: VoidFunction) => {
			try {
				await reviewDataPoint.mutateAsync(variables)
				notify.success({
					message: "Mark as reviewed",
					description: `Image reviewed successfully at ${dateFormatter(today().toISOString(), { hasYear: false })}`,
				})
				onSuccess?.()
			} catch (_) {}
		},
		[notify, reviewDataPoint],
	)
	const updateImageReviewedHandler = async () => {
		if (reviewDisabled || reviewDataPoint.isPending) {
			return
		}
		if (!!studyTemplateId) {
			discloseStudyForm.open()
		} else {
			await reviewDataPointHandler(null)
		}
	}
	const handleOnFinish = async (answers: TemplatedFormParamsAnswer[]) => {
		await reviewDataPointHandler(
			{
				template: studyTemplateId!,
				answers,
			},
			discloseStudyForm.close,
		)
	}
	const copyHandler = async () => {
		if (copySuccess) return
		await copyToClipboard(window.location.href)
		setCopySuccess(true)
		setTimeout(() => {
			setCopySuccess(false)
		}, 3000)
	}

	useHotkeys("r", updateImageReviewedHandler, { enabled: !reviewDisabled })
	useHotkeys("ctrl+c, meta+c", async () => {
		if (hasSelected()) {
			return
		}
		await copyHandler()
	})

	if (!patient) {
		return null
	}

	return (
		<div
			{...props}
			className={cn(
				"flex flex-col gap-6 overflow-y-auto bg-white p-8",
				className,
			)}
		>
			<Profile patient={patient} />

			<div className="flex flex-col gap-4">
				<h3 className="font-semibold">Details</h3>

				<div className="flex flex-col gap-2">
					<div className="flex gap-2">
						<CalendarOutlined aria-hidden className="text-2xl text-navy-60" />
						<p className="text-lg">
							{dateFormatter(capturedAt, { hasYear: false })}
						</p>
						{!isProviderAnon && !isReviewed && (
							<p className="flex items-center justify-start gap-1 rounded-2xl bg-blue-0 px-2 text-sm text-blue-60">
								New
							</p>
						)}
					</div>

					<WeightDetail
						fullName={patient?.fullName}
						dataPoint={dataPoint}
						previousDataPoint={previousDataPoint}
					/>
				</div>

				<div className="flex flex-col gap-2">
					{isProvider && (
						<>
							<div className="flex gap-3">
								<Wrap
									condition={!isReviewed && !disabled}
									with={(children) => (
										<Tooltip.Dark
											title={
												<ShortcutLabel label="Mark as reviewed" shortcut="r" />
											}
										>
											{children}
										</Tooltip.Dark>
									)}
								>
									<Button
										disabled={reviewDisabled}
										className="w-full"
										icon={reviewDisabled ? null : <CheckOutlined aria-hidden />}
										loading={reviewDataPoint.isPending}
										aria-readonly={isReviewed}
										onClick={updateImageReviewedHandler}
									>
										{reviewButtonLabel}
									</Button>
								</Wrap>

								<Tooltip.Dark
									title={
										<ShortcutLabel
											label="Copy link"
											shortcut={isMac || isIOS ? "⌘C" : "Ctrl+C"}
										/>
									}
								>
									<Button
										aria-label={
											copySuccess ? "Link copied to clipboard" : "Copy link"
										}
										icon={
											copySuccess ? (
												<CheckOutlined aria-hidden />
											) : (
												<LinkOutlined aria-hidden />
											)
										}
										className={cn(
											"shrink-0",
											copySuccess &&
												"pointer-events-none bg-white text-mint-100",
										)}
										onClick={copyHandler}
									/>
								</Tooltip.Dark>
							</div>

							{!discloseStudyForm.isOpen && (
								<ErrorStatus
									show={reviewDataPoint.isError && !reviewDataPoint.isPending}
								>
									{reviewDataPoint.error?.message}
								</ErrorStatus>
							)}
						</>
					)}
				</div>
			</div>

			{patientId && clinicalNotes.enabled && (
				<ClinicalNotes patientId={patientId} dataPoint={dataPoint} />
			)}

			{discloseStudyForm.isOpen && (
				<Modal
					open={discloseStudyForm.isOpen}
					title="Screening questionnaire"
					okText="Submit"
					cancelText="Cancel"
					okType="primary"
					onOk={form.submit}
					closable={false}
					onCancel={discloseStudyForm.close}
					confirmLoading={reviewDataPoint.isPending}
				>
					<TemplatedForm
						form={form}
						questions={studyQuestions}
						onFinish={handleOnFinish}
					/>
					<ErrorStatus
						show={reviewDataPoint.isError && !reviewDataPoint.isPending}
					>
						{reviewDataPoint.error?.message}
					</ErrorStatus>
				</Modal>
			)}
		</div>
	)
}

const Profile = ({ patient }: { patient: Patient }) => {
	const { clinicalNotes } = useFlag("clinicalNotes")
	const { data: note, isFetched } = useProfileNote(patient.id)
	const { data: { isProvider, isProviderAnon } = {} } = useUserRole()
	const organization = useOrganization()
	const { data: unreadMessagesCount } = useUnreadMessagesCount(
		isProvider ? patient.id : undefined,
	)
	const withProfileNote =
		clinicalNotes.enabled &&
		!!note &&
		(note.content.trim().length > 0 || note.entries.length > 0)

	if (!isFetched) {
		return null
	}

	return (
		<Wrap
			condition={withProfileNote}
			with={(children) => (
				<Disclosure summary={children}>
					<EditProfileNote variant="borderless" patientId={patient.id} />
				</Disclosure>
			)}
		>
			<div
				className={cn(
					"flex w-full items-center gap-4",
					!withProfileNote && "rounded-xl border border-mint-20 p-4",
				)}
			>
				<Avatar
					className="min-h-16 min-w-16"
					icon={isProviderAnon ? <UserOutlined aria-hidden /> : undefined}
				>
					{!isProviderAnon ? patient?.fullName : undefined}
				</Avatar>

				<div className="flex min-w-0 flex-col gap-2">
					<div className="flex items-center gap-2">
						<h3 className="truncate font-headings text-2xl font-semibold">
							{isProvider && patient?.fullName
								? patient.fullName
								: DEFAULT_PLACEHOLDER}
						</h3>

						<div className="min-w-fit text-xl text-navy-60">
							{`(${
								isProvider && patient?.age ? patient.age : DEFAULT_PLACEHOLDER
							} years)`}
						</div>
					</div>

					{!isProviderAnon && patient && (
						<div className="flex items-center gap-2">
							<Voice.CallToAction
								size="small"
								show={isProvider && organization?.hasSend}
								patient={patient}
							/>

							<Chat.CallToAction
								size="small"
								view={!!organization?.hasView}
								send={!!organization?.hasSend}
								patient={patient}
								unreadMessagesCount={unreadMessagesCount}
							/>
						</div>
					)}
				</div>
			</div>
		</Wrap>
	)
}

const WeightDetail = ({
	dataPoint,
	previousDataPoint,
	fullName,
}: {
	dataPoint: PatientDetailItem
	previousDataPoint?: PatientDetailItem
	fullName?: string
}) => {
	const { Icon, percentageText } = useMemo(() => {
		const prev = previousDataPoint?.weightInKilogramsForce
		const curr = dataPoint.weightInKilogramsForce

		if (!prev || prev === curr) {
			return { Icon: WeightIcon.Stable, percentageText: null }
		}

		const pct = ((curr - prev) / prev) * 100
		const isGaining = pct > 0

		return {
			Icon: isGaining ? WeightIcon.Gaining : WeightIcon.Losing,
			percentageText: `${isGaining ? "+" : ""}${pct.toFixed(1)}%`,
		}
	}, [
		previousDataPoint?.weightInKilogramsForce,
		dataPoint.weightInKilogramsForce,
	])

	return (
		<div className="flex gap-2">
			<Icon className="size-6 text-navy-60" aria-hidden />
			<WeightLabel fullName={fullName} dataPoint={dataPoint} />
			{percentageText && (
				<span className="text-lg text-navy-60">{percentageText}</span>
			)}
		</div>
	)
}

function ClinicalNotes({
	dataPoint,
	patientId,
}: {
	dataPoint: PatientDetailItem
	patientId: string
}) {
	const { clinicalEscalations } = useFlag("clinicalEscalations")
	const { notify } = useNotification()
	const { data: { isProviderAnon } = {} } = useUserRole()
	const { data: note } = useScreeningNote(dataPoint.id)
	const { mutate, isPending } = useScreeningNoteUpsert(dataPoint.id, patientId)
	const { data: { screeningNoteLabels = [] } = {} } =
		useConfigClinicalNoteLabels()
	type FormValues = Pick<ScreeningNote, "labels" | "observations">
	const [form] = Form.useForm<FormValues>()
	const isChanged =
		Form.useWatch(
			(values) =>
				JSON.stringify({
					labels: values.labels ?? [],
					observations: values.observations ?? "",
				}),
			form,
		) !==
		JSON.stringify({
			labels: note?.labels ?? [],
			observations: note?.observations ?? "",
		})
	const formAriaLabel = `Add clinical note on data point captured at ${dateFormatter(dataPoint.capturedAt, { hasYear: false })}`
	const options = useMemo(
		() =>
			Object.entries(groupBy(screeningNoteLabels, (label) => label.group || ""))
				.map(([group, items]) => {
					const opts = items.map((item) => ({
						label: item.displayName,
						value: item.key,
						color: item.color ?? "default",
					}))
					return group ? { label: humanReadable(group), options: opts } : opts
				})
				.flat(),
		[screeningNoteLabels],
	)
	const handleFinish = useCallback(
		({ labels, observations }: FormValues) => {
			mutate(
				{ version: note?.version ?? 0, labels, observations },
				{
					onError(error) {
						const common = "An unexpected error occurred"
						notify.error({
							message: error.message
								? `${common}: ${error.message}`
								: `${common}. Please, try again.`,
						})
					},
				},
			)
		},
		[mutate, note?.version, notify],
	)

	useEffect(() => {
		form.setFieldsValue({
			...note,
		})
	}, [form, note])

	useHotkeys("mod+enter", form.submit, {
		enabled: isChanged,
		enableOnFormTags: ["TEXTAREA"],
	})

	return (
		<Form<FormValues>
			form={form}
			autoComplete="off"
			onFinish={handleFinish}
			aria-label={formAriaLabel}
		>
			<fieldset disabled={isPending} className="flex flex-col gap-2">
				<h3 className="font-semibold">Clinical note</h3>

				<Form.Item<FormValues> name="labels" noStyle>
					<TagSelect
						buttonProps={{ children: "Add label" }}
						selectProps={{ options, "aria-label": "Select label" }}
					/>
				</Form.Item>

				<Form.Item<FormValues> name="observations" noStyle>
					<Input.TextArea
						aria-label="Observations"
						placeholder="Add your clinical note here..."
						autoSize={{ minRows: 2, maxRows: 6 }}
					/>
				</Form.Item>

				{clinicalEscalations.enabled && !isChanged && !isProviderAnon && (
					<CreateEscalation
						payload={{
							linkedScreeningIds: [dataPoint.id],
							observations: note?.observations ?? "",
						}}
					>
						Escalate screening
					</CreateEscalation>
				)}

				<Form.Item noStyle>
					<div
						className={cn(
							"hidden items-center justify-end gap-3",
							isChanged && "flex",
						)}
					>
						<Button
							onClick={() => {
								form.setFieldsValue({
									...note,
								})
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							icon={<SaveOutlined aria-hidden />}
							loading={isPending}
						>
							Save changes
						</Button>
					</div>
				</Form.Item>
			</fieldset>
		</Form>
	)
}
